/**
 * React Query Keys for Meal Plan
 * Canonical key structure following best practices from TanStack Query docs
 *
 * Hierarchy:
 * - all: Base key for all meal plan queries
 * - user: All meal plans for a specific user
 * - week: Specific week's meal plan entries
 * - slot: Individual meal slot (day + mealType)
 *
 * Usage:
 * - Use specific keys for targeted invalidations
 * - Use parent keys to invalidate entire branches
 *
 * Example:
 * queryClient.invalidateQueries({ queryKey: mealPlanKeys.week(userId, weekId) })
 * // Invalidates all queries for that week, but not other weeks
 */

export const mealPlanKeys = {
  /**
   * Base key for all meal plan data
   */
  all: ['mealPlan'] as const,

  /**
   * All meal plan data for a specific user
   * @param userId - User ID
   */
  user: (userId: string) => [...mealPlanKeys.all, userId] as const,

  /**
   * All entries for a specific week
   * @param userId - User ID
   * @param weekId - Week identifier (format: "YYYY-WW-Timezone")
   */
  week: (userId: string, weekId: string) =>
    [...mealPlanKeys.user(userId), 'week', weekId] as const,

  /**
   * Specific meal slot (day + mealType)
   * @param userId - User ID
   * @param weekId - Week identifier
   * @param dayIndex - Day index (0-6)
   * @param mealType - Meal type (breakfast|lunch|dinner|snack)
   */
  slot: (userId: string, weekId: string, dayIndex: number, mealType: string) =>
    [...mealPlanKeys.week(userId, weekId), 'slot', dayIndex, mealType] as const,

  /**
   * Conflict check for a specific slot (before adding)
   * @param userId - User ID
   * @param weekId - Week identifier
   * @param dayIndex - Day index (0-6)
   * @param mealType - Meal type
   */
  conflictCheck: (userId: string, weekId: string, dayIndex: number, mealType: string) =>
    [...mealPlanKeys.slot(userId, weekId, dayIndex, mealType), 'conflict'] as const,
} as const;

/**
 * Type exports for type-safe query keys
 */
export type MealPlanKeys = typeof mealPlanKeys;
// `all` is a constant tuple, not a function — use typeof instead of ReturnType
export type MealPlanAllKey = typeof mealPlanKeys.all;
export type MealPlanUserKey = ReturnType<typeof mealPlanKeys.user>;
export type MealPlanWeekKey = ReturnType<typeof mealPlanKeys.week>;
export type MealPlanSlotKey = ReturnType<typeof mealPlanKeys.slot>;
export type MealPlanConflictCheckKey = ReturnType<typeof mealPlanKeys.conflictCheck>;
